import { List, makeStyles, Paper } from "@material-ui/core";
import React from "react";
import { useHistory, useLocation } from "react-router-dom";
import { useFieldLookups, useLookup } from "@ofqual/portal-core/core/hooks";
import { ListItem } from "@ofqual/portal-components/components/List/ListItem";
import { ItemsState } from "../../redux/state";
import { Message } from "@material-ui/icons";
import {
  InformationRequest,
  InformationRequestStatus,
} from "../../domain/InformationRequest";
import {
  formatUtcDate,
  standardDateFormats,
} from "@ofqual/portal-core/core/utils/dateUtils";
import { useAuth } from "@ofqual/portal-core/core/hooks/useAuth";
import { UserType } from "../../redux/types";
import { useMemo } from "react";
import { cachingAoService } from "../../services";
import { authSelector } from "../../redux/selectors";

const SEARCH_STATE_KEY = "search";

const useListStyles = makeStyles((theme) => ({
  active: {
    ...theme.list.itemPrimary,
  },
  disabled: {
    ...theme.list.itemDisabled,
  },
  submissionRowDetails: {
    paddingRight: "20px",
  },
}));

type ItemsListProps = {
  state: ItemsState;
  caoRequestTypeLookups: ReturnType<typeof useLookup>[0];
};

export const ItemsList: React.FC<ItemsListProps> = ({
  state,
  caoRequestTypeLookups,
}) => {
  const classes = useListStyles();
  const { response } = state;
  const history = useHistory();
  const { pathname, search } = useLocation();
  const navigate = (id: string) =>
    history.push(`${pathname}/${id}`, { [SEARCH_STATE_KEY]: search });
  const { userType } = useAuth(authSelector);
  const isOfqualUser = userType === UserType.Ofqual;

  const orgIdsPresent = useMemo(
    () =>
      response.data
        .filter((x) => !!x.firstRecipientId)
        .map((item: InformationRequest) => item.firstRecipientId as number)
        .flat(),
    [response]
  );

  const orgLookups = useFieldLookups(cachingAoService, orgIdsPresent);
  const getOrgName = (item: InformationRequest) => {
    if (item.isSendToMultiple === true) return "Multiple";

    if (!!item.firstRecipientId && !!orgLookups)
      return orgLookups[item.firstRecipientId];

    return null;
  };

  const getRequestTypeString = (item: InformationRequest) =>
    caoRequestTypeLookups.find((x) => x.id === item.requestType)?.label || "";

  return (
    <Paper>
      <List disablePadding data-test="inbox-list">
        {response.data.map((item) => {
          const orgName = getOrgName(item);
          return (
            <ListItem
              key={item.id}
              className={classes.active}
              onClick={() => navigate(item.id)}
              dataTest="row"
              Icon={Message}
              primaryText={
                !!item.subject
                  ? item.subject
                  : `Contact Awarding Organisation ${getRequestTypeString(
                      item
                    )}`
              }
              secondaryDetails={[
                { heading: "Type", value: getRequestTypeString(item) },
                { heading: "Created by", value: item.createdByDisplayName },
                {
                  heading: "Created date",
                  value: formatUtcDate(
                    item.createdDate,
                    standardDateFormats.DATE_TIME
                  ),
                },
                {
                  heading: "Nearest due date",
                  value: !!item.minDueDate
                    ? formatUtcDate(
                        item.minDueDate,
                        standardDateFormats.DATE_TIME
                      )
                    : null,
                },
                {
                  heading: "Recipient",
                  value: isOfqualUser && !!orgName ? orgName : null,
                },
              ]}
              chipTexts={
                isOfqualUser
                  ? [`Status: ${InformationRequestStatus[item.status]}`]
                  : [`Status: ${[item.aoStatus]}`]
              }
            />
          );
        })}
      </List>
    </Paper>
  );
};
